import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Target, TrendingUp, Bot, Settings } from 'lucide-react';
import ProjectOverview from './Dashboard/ProjectOverview';
import ProjectCreator from './Dashboard/ProjectCreator';
import ReportsViewer from './Dashboard/ReportsViewer';
import AgentMonitor from './Dashboard/AgentMonitor';
import ConfigEditor from './Dashboard/ConfigEditor';
import NotificationSystem from './Dashboard/NotificationSystem';
import { agentService } from './API/agentService';
import './AgentsDashboard.css';

const AgentsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    // Set active tab based on current route
    const path = location.pathname.split('/').pop();
    if (['overview', 'projects', 'reports', 'agents', 'config'].includes(path)) {
      setActiveTab(path);
    }
  }, [location.pathname]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [projectsData, agentsData] = await Promise.all([
        agentService.getProjects(),
        agentService.getAgentStatus()
      ]);
      setProjects(projectsData);
      setAgents(agentsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/college/agents/${tab}`);
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'projects', label: 'Projects', icon: Target },
    { key: 'reports', label: 'Reports', icon: TrendingUp },
    { key: 'agents', label: 'Agents', icon: Bot },
    { key: 'config', label: 'Config', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="agents-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner" role="status" aria-label="Loading agents dashboard"></div>
          <div>
            <p>Loading College Journey Agents Dashboard...</p>
            <p className="stat-detail">Preparing project status, agent monitoring, and development insights</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agents-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>College Journey Agents</h1>
          <p>Manage projects, monitor progress, and coordinate development across the College Journey platform</p>
        </div>

        <nav className="dashboard-tabs" role="tablist" aria-label="Dashboard navigation">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`${tab.key}-panel`}
              id={`${tab.key}-tab`}
            >
              <tab.icon className="tab-icon" size={18} aria-hidden="true" />
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="dashboard-content" role="main">
        <div role="tabpanel" id={`${activeTab}-panel`} aria-labelledby={`${activeTab}-tab`}>
          <Routes>
            <Route
              path="overview"
              element={
                <ProjectOverview
                  projects={projects}
                  agents={agents}
                  onRefresh={loadDashboardData}
                />
              }
            />
            <Route
              path="projects"
              element={
                <ProjectCreator
                  projects={projects}
                  onProjectCreated={loadDashboardData}
                  onProjectUpdated={loadDashboardData}
                />
              }
            />
            <Route
              path="reports"
              element={
                <ReportsViewer
                  projects={projects}
                  agents={agents}
                />
              }
            />
            <Route
              path="agents"
              element={
                <AgentMonitor
                  agents={agents}
                  onRefresh={loadDashboardData}
                />
              }
            />
            <Route
              path="config"
              element={
                <ConfigEditor
                  onConfigUpdated={loadDashboardData}
                />
              }
            />
            <Route
              path=""
              element={
                <ProjectOverview
                  projects={projects}
                  agents={agents}
                  onRefresh={loadDashboardData}
                />
              }
            />
          </Routes>
        </div>
      </main>
      <NotificationSystem />
    </div>
  );
};

export default AgentsDashboard;